/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import * as React from 'react';

import { Heading, IModuleProps } from '@msdyn365-commerce-modules/utilities';

import { IFarmlandsAccountAddressTileData } from './farmlands-account-address-tile.data';
import { IFarmlandsAccountAddressTileProps } from './farmlands-account-address-tile.props.autogenerated';
import DescriptionComponent from './components/farmlands-account-address-tile-description';
import LinksComponent from './components/farmlands-account-address-tile-links';
import { isShareholder } from '../../common/method/functions';

export interface IAccountAddressTileViewProps
    extends IFarmlandsAccountAddressTileProps<IFarmlandsAccountAddressTileData> {
    AccountAddressTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
    description?: React.ReactNode;
}

/**
 *
 * AccountAddress component
 * @extends {React.PureComponent<IFarmlandsAccountAddressTileProps<IFarmlandsAccountAddressTileData>>}
 */
class AccountAddressTile extends React.PureComponent<
    IFarmlandsAccountAddressTileProps<IFarmlandsAccountAddressTileData>
> {
    public render(): JSX.Element {
        const { config, data } = this.props;
        const { className, heading, links, shareholderText } = config;
        const { address } = data;
        const addressCount = (address && address.result && address.result.length) || 0;
        const description = this._formatAddressDescription(addressCount);

        const isShareholderAccount: boolean = isShareholder(data.cart.result!.cart);

        const viewProps = {
            ...this.props,
            className: className,
            AccountAddressTile: {
                moduleProps: this.props,
                className: classnames('ms-account-address-tile', config.className)
            },
            heading: heading && (
                <Heading className='ms-account-address-tile__heading' headingTag={heading.tag} text={heading.text} />
            ),
            links: !isShareholderAccount && links && links.length > 0 && <LinksComponent {...{ links }} />,
            description: !isShareholderAccount ? (
                <DescriptionComponent description={description} />
            ) : (
                <DescriptionComponent description={shareholderText!} />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _formatAddressDescription(count: number): string {
        const numberWords = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
        if (count <= 1) {
            return `You have ${numberWords[count]} address saved.`;
        }
        if (count < 10) {
            return `You have ${numberWords[count]} addresses saved.`;
        }
        return `You have ${count} addresses saved.`;
    }
}

export default AccountAddressTile;
